import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import GlobalIndicators from '../components/GlobalIndicators';
import RegulatoryConfirmationModal from '../components/RegulatoryConfirmationModal';
import SmartReservationCard from '../components/SmartReservationCard';
import io from 'socket.io-client'; // [Novo]
import TransferModal from '../components/TransferModal';
import BedAvailabilityAlert from '../components/BedAvailabilityAlert'; // [Novo]
import UpdateNotification from '../components/UpdateNotification'; // [Smart Refresh]
import { AlertTriangle, Clock, ArrowRightCircle } from 'lucide-react';

export default function ReservationDashboard() {
    const [reservations, setReservations] = useState([]);
    const [beds, setBeds] = useState([]); // Needed for global indicators
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');

    // Census States
    const [sectors, setSectors] = useState([]);
    const [selectedSector, setSelectedSector] = useState('');
    const [longStayOnly, setLongStayOnly] = useState(false);

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [selectedReservation, setSelectedReservation] = useState(null);
    const [activeTab, setActiveTab] = useState('today'); // 'today', 'future'
    const navigate = useNavigate(); // [Novo] Hook de navegação

    // [Smart Refresh] Estados
    const [pendingUpdates, setPendingUpdates] = useState(null);
    const [lastChecked, setLastChecked] = useState(new Date());

    useEffect(() => {
        let intervalId = null;
        let socket = null;

        const initSmartRefresh = async () => {
            fetchData(); // Initial load

            try {
                // 1. Buscar configurações
                const settingsRes = await api.get('/settings');
                const settings = {};
                settingsRes.data.forEach(s => settings[s.key] = s.value);

                const mode = settings.dashboard_update_mode || 'polling';
                const intervalMs = parseInt(settings.dashboard_polling_interval) || 30000;

                console.log(`Smart Refresh iniciado em modo: ${mode.toUpperCase()} (${intervalMs}ms)`);

                if (mode === 'websocket') {
                    // MODO WEBSOCKET
                    const SOCKET_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
                    socket = io(SOCKET_URL);

                    socket.on('connect', () => console.log('🟢 WS Conectado'));
                    socket.on('reservation:update', (data) => {
                        console.log('⚡ WS Event:', data);
                        silentFetchForUpdates();
                    });

                } else {
                    // MODO POLLING
                    intervalId = setInterval(silentFetchForUpdates, intervalMs);
                }

            } catch (error) {
                console.error('Erro ao iniciar Smart Refresh:', error);
                intervalId = setInterval(silentFetchForUpdates, 30000);
            }
        };

        initSmartRefresh();

        return () => {
            if (intervalId) clearInterval(intervalId);
            if (socket) socket.disconnect();
        };
    }, [filterStatus, activeTab, selectedSector, longStayOnly]);

    // [Smart Refresh] Logic
    const silentFetchForUpdates = async () => {
        try {
            const params = {};
            if (activeTab !== 'census' && filterStatus !== 'all') params.status = filterStatus;

            const resResponse = await api.get('/reservations', { params });
            let newRes = resResponse.data;
            const today = new Date().toISOString().split('T')[0];

            if (activeTab === 'today') {
                newRes = newRes.filter(r => (r.scheduled_date <= today || !r.scheduled_date) && r.status !== 'fulfilled' && r.status !== 'canceled');
            } else if (activeTab === 'future') {
                newRes = newRes.filter(r => r.scheduled_date && r.scheduled_date > today && r.status !== 'fulfilled' && r.status !== 'canceled');
            }

            // Comparação
            const currentIds = reservations.map(r => r.id + r.status).join(',');
            const newIds = newRes.map(r => r.id + r.status).join(',');

            if (currentIds !== newIds) {
                console.log('Smart Refresh: Mudança confirmada');
                setPendingUpdates({ reservations: newRes });
            }
        } catch (error) {
            console.error('Erro no fetch silencioso', error);
        }
    };

    const handleApplyUpdates = async () => {
        if (pendingUpdates?.reservations) {
            setLoading(true);
            setReservations(pendingUpdates.reservations);
            // Refresh rápido de leitos/setores para garantir
            const [bedRes, secRes] = await Promise.all([api.get('/beds'), api.get('/sectors')]);
            setBeds(bedRes.data);
            setSectors(secRes.data);

            setPendingUpdates(null);
            setLoading(false);
        }
    };

    const fetchData = async () => {
        try {
            const params = {};
            // [Fix] Se estiver no Censo, precisamos de TODAS as reservas para identificar transferências (badges)
            // Se estiver em abas de reserva, respeita o filtro
            if (activeTab !== 'census' && filterStatus !== 'all') {
                params.status = filterStatus;
            }

            const [resResponse, bedResponse, secResponse] = await Promise.all([
                api.get('/reservations', { params }),
                api.get('/beds'),
                api.get('/sectors')
            ]);

            let allRes = resResponse.data;
            const today = new Date().toISOString().split('T')[0];

            if (activeTab === 'today') {
                allRes = allRes.filter(r =>
                    (r.scheduled_date <= today || !r.scheduled_date) &&
                    r.status !== 'fulfilled' &&
                    r.status !== 'canceled'
                );
            } else if (activeTab === 'future') {
                allRes = allRes.filter(r =>
                    r.scheduled_date && r.scheduled_date > today &&
                    r.status !== 'fulfilled' &&
                    r.status !== 'canceled'
                );
            }

            // [Novo] Se buscamos tudo (por causa do Censo) mas estamos em outra aba e com filtro ativado,
            // precisamos filtrar em memória para não mostrar cards errados se o usuário trocar de aba rápido
            if (activeTab !== 'census' && filterStatus !== 'all') {
                allRes = allRes.filter(r => r.status === filterStatus);
            }

            setReservations(allRes);
            setBeds(bedResponse.data);
            setSectors(secResponse.data);
            setLoading(false);
        } catch (error) {
            console.error('Erro ao buscar dados:', error);
            setLoading(false);
        }
    };

    const calculateDaysHosted = (dateString) => {
        if (!dateString) return 0;
        const admission = new Date(dateString);
        const today = new Date();
        const diffTime = Math.abs(today - admission);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const handleConfirmClick = (reservation) => {
        setSelectedReservation(reservation);
        setShowModal(true);
    };

    const handleRegulatoryConfirm = async (data) => {
        try {
            if (selectedReservation?.type === 'transfer') {
                // [Check] Verificar duplicidade antes de criar
                const isAlreadyTransferring = reservations.some(r =>
                    r.patient_name === selectedReservation.patient_name &&
                    r.status === 'confirmed' &&
                    r.observation?.includes('TRANSFERÊNCIA')
                );

                if (isAlreadyTransferring) {
                    alert('Este paciente já possui uma transferência em andamento!');
                    return;
                }

                // 1. Criar NOVA reserva (Transferência)
                const resResponse = await api.post('/reservations', {
                    patient_name: selectedReservation.patient_name,
                    patient_gender: selectedReservation.patient_gender,
                    patient_condition: selectedReservation.patient_condition,
                    priority: 'high',
                    origin: selectedReservation.origin,
                    sector_id: data.targetSectorId,
                    status: 'confirmed',
                    allocation_type: 'specific',
                    specific_bed_id: data.targetBedId,
                    observation: `TRANSFERÊNCIA - LEITO_ORIGEM:${selectedReservation.bed_id} - ${data.observation || ''}`
                });

                // 2. Criar Alerta de Preparação para Enfermagem (ServiceDashboard)
                // Enfermagem prepara o leito -> Depois chama maqueiro/confirmação
                await api.post('/service-alerts', {
                    service_type: 'nursing',
                    bed_id: data.targetBedId,
                    priority: 3, // Alta
                    status: 'pending',
                    metadata: JSON.stringify({
                        type: 'bed_preparation_transfer', // [CHANGED]
                        admission_type: 'Transferência Interna', // [NEW] Label para diferenciar
                        reservation_id: resResponse.data.id,
                        patient_name: selectedReservation.patient_name,
                        origin_bed_id: selectedReservation.bed_id,
                        origin_sector_name: selectedReservation.origin
                    })
                });

                // 3. Criar Alerta para Lavanderia (Kit Admissão - Transferência) [NEW]
                await api.post('/service-alerts', {
                    service_type: 'laundry',
                    bed_id: data.targetBedId,
                    priority: 3, // Alta
                    status: 'pending',
                    metadata: JSON.stringify({
                        type: 'admission_kit',
                        admission_type: 'Transferência Interna', // Label importante para Backend nao duplicar check
                        patient_name: selectedReservation.patient_name,
                        patient_gender: selectedReservation.patient_gender,
                        patient_condition: selectedReservation.patient_condition
                    })
                });

                alert('Transferência solicitada! Enfermagem e Lavanderia foram notificadas.');
            } else {
                // É uma Reserva normal: Iniciar fluxo de internação "Aguardando Check-in"
                await api.put(`/reservations/${data.reservationId}`, {
                    status: 'waiting_admission', // [CHANGED] Antes 'confirmed', agora aguarda check-in
                    regulatory_reason: data.reason,
                    allocation_type: data.allocationType,
                    specific_bed_id: data.specificBedId,
                    observation: data.observation,
                    confirmed_at: new Date().toISOString()
                });
            }
            fetchData();
        } catch (error) {
            alert('Erro ao processar: ' + error.message);
        }
    };

    const handleCancel = async (id) => {
        if (!window.confirm('Tem certeza que deseja cancelar esta reserva?')) return;

        const reason = window.prompt('Motivo do cancelamento (Opcional):');

        try {
            await api.put(`/reservations/${id}`, {
                status: 'canceled',
                cancellation_reason: reason || null
            });
            fetchData();
        } catch (error) {
            alert('Erro ao cancelar: ' + error.message);
        }
    };

    const handleArchive = async (id) => {
        if (!window.confirm('Tem certeza que deseja arquivar este card? (O paciente já está internado e o fluxo concluído)')) return;

        try {
            // "fulfilled" remove da lista de ativos (hoje/futuros)
            await api.put(`/reservations/${id}`, {
                status: 'fulfilled',
                fulfilled_at: new Date().toISOString()
            });
            fetchData();
        } catch (error) {
            alert('Erro ao arquivar: ' + error.message);
        }
    };

    // Helper para verificar se leito está em processo de transferência (Origem)
    const isTransferredOrigin = (bedId) => {
        return reservations.some(r =>
            r.status === 'confirmed' &&
            r.observation &&
            r.observation.includes(`LEITO_ORIGEM:${bedId}`)
        );
    };

    // --- Render ---

    return (
        <div className="container-fluid mt-4 pb-20"> {/* pb-20 for bottom nav space */}
            <div className="d-flex justify-content-between align-items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                        <i className="bi bi-clipboard-data"></i> NIR Avançado
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Centro de Decisão Tática e Operacional</p>
                </div>
                <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg gap-2">
                    {/* Switch de Data */}
                    <div className="flex border-r border-gray-300 pr-2 mr-2">
                        <button
                            className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${activeTab === 'today'
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => setActiveTab('today')}
                        >
                            Para Hoje
                        </button>

                        <button
                            className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${activeTab === 'future'
                                ? 'bg-purple-600 text-white shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => setActiveTab('future')}
                        >
                            <i className="bi bi-calendar-event me-1"></i> Agendadas
                        </button>
                        <button
                            className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${activeTab === 'census'
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => setActiveTab('census')}
                        >
                            <i className="bi bi-people-fill me-1"></i> Censo (Internados)
                        </button>
                    </div>

                    {['all', 'pending', 'confirmed'].map(status => (
                        <button
                            key={status}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${filterStatus === status
                                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                                }`}
                            onClick={() => setFilterStatus(status)}
                        >
                            {status === 'all' ? 'Todas' : status === 'pending' ? 'Pendentes' : 'Confirmadas'}
                        </button>
                    ))}
                </div>
            </div>

            {/* Top Section: Indicators & Filters */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">

                {/* [Smart Refresh] Notificação Flutuante */}
                {pendingUpdates && (
                    <UpdateNotification
                        onUpdate={handleApplyUpdates}
                        count={pendingUpdates.reservations ? pendingUpdates.reservations.length : 1}
                    />
                )}

                {/* Left: Global Indicators (Condensed) */}
                <div className="lg:col-span-7 xl:col-span-6">
                    <GlobalIndicators reservations={reservations} beds={beds} />
                </div>

                {/* Right: Filters & Actions */}
                <div className="lg:col-span-5 xl:col-span-6 flex flex-col justify-content-center">
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 h-full">
                        <div className="flex items-center gap-2 mb-3">
                            <i className="bi bi-sliders2 text-indigo-600"></i>
                            <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 m-0">Filtros Operacionais</h4>
                        </div>

                        <div className="flex flex-wrap gap-3 items-center">
                            {/* Sector Filter */}
                            <div className="flex-1 min-w-[200px]">
                                <label className="text-xs text-gray-500 font-semibold mb-1 block">Filtrar por Setor</label>
                                <select
                                    className="form-select text-sm w-100 border-gray-300 rounded-lg"
                                    value={selectedSector}
                                    onChange={(e) => setSelectedSector(e.target.value)}
                                >
                                    <option value="">Todos os Setores</option>
                                    {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>

                            {/* Census Specific Filter */}
                            {activeTab === 'census' && (
                                <div className="flex-1 min-w-[200px]">
                                    <label className="text-xs text-gray-500 font-semibold mb-1 block">Foco Clínico</label>
                                    <label className={`flex items-center gap-2 text-sm cursor-pointer px-3 py-2 rounded-lg border transition-all ${longStayOnly ? 'bg-red-50 border-red-200 text-red-700' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                                        <input
                                            type="checkbox"
                                            checked={longStayOnly}
                                            onChange={(e) => setLongStayOnly(e.target.checked)}
                                            className="form-checkbox rounded text-red-600 focus:ring-red-500"
                                        />
                                        <span className="font-medium">Longa Permanência (&gt; 10 dias)</span>
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Alerta Flutuante */}
            <BedAvailabilityAlert reservations={reservations} beds={beds} />

            {/* Content Area */}
            {loading ? (
                <div className="text-center p-12"><div className="spinner-border text-primary"></div></div>
            ) : (
                <>
                    {activeTab === 'census' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {beds.filter(b => {
                                if (b.statusId !== 5) return false;
                                if (selectedSector && b.sectorId.toString() !== selectedSector) return false;

                                if (longStayOnly) {
                                    const lastHistory = b.history && b.history.length > 0 ? b.history[b.history.length - 1] : {};
                                    const days = calculateDaysHosted(lastHistory.date);
                                    if (days <= 10) return false;
                                }
                                return true;
                            }).length === 0 ? (
                                <div className="col-span-full text-center py-12 text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                                    <h4>Nenhum paciente internado encontrado com os filtros atuais.</h4>
                                </div>
                            ) : (
                                beds.filter(b => {
                                    if (b.statusId !== 5) return false;
                                    if (selectedSector && b.sectorId.toString() !== selectedSector) return false;
                                    if (longStayOnly) {
                                        const lastHistory = b.history && b.history.length > 0 ? b.history[b.history.length - 1] : {};
                                        const days = calculateDaysHosted(lastHistory.date);
                                        if (days <= 10) return false;
                                    }
                                    return true;
                                }).map(bed => {
                                    const lastHistory = bed.history && bed.history.length > 0 ? bed.history[bed.history.length - 1] : {};
                                    const daysHosted = calculateDaysHosted(lastHistory.date);
                                    const sectorName = sectors.find(s => s.id === bed.sectorId)?.name || `Setor ${bed.sectorId}`;
                                    const isTransfering = isTransferredOrigin(bed.id);

                                    return (
                                        <div key={bed.id} className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-l-4 p-4 hover:shadow-md transition-all ${isTransfering ? 'border-orange-500 bg-orange-50/50' : 'border-indigo-500'}`}>
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div>
                                                    <span className="badge bg-indigo-100 text-indigo-700 mb-2">{sectorName}</span>
                                                    {isTransfering && <span className="badge bg-orange-100 text-orange-700 mb-2 ms-2 animate-pulse">Em Transferência</span>}
                                                    <h5 className="font-bold text-gray-800 dark:text-white">Leito {bed.number}</h5>
                                                    <h6 className="text-indigo-600 font-semibold">{lastHistory.patientName || 'Paciente Sem Nome'}</h6>
                                                </div>
                                                <i className="bi bi-person-circle text-gray-300 text-3xl"></i>
                                            </div>

                                            <div className="space-y-2 mb-4">
                                                <div className="flex items-center text-xs text-gray-500">
                                                    <span className="w-20 font-semibold">Admissão:</span>
                                                    <span>{new Date(lastHistory.date || new Date()).toLocaleDateString()}</span>
                                                </div>
                                                <div className="flex items-center text-xs text-gray-500">
                                                    <span className="w-20 font-semibold">Tempo:</span>
                                                    <span className={`badge ${daysHosted > 10 ? 'bg-red-100 text-red-700' : daysHosted > 5 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                                                        {daysHosted} dias
                                                    </span>
                                                </div>
                                            </div>

                                            <button
                                                className="btn btn-outline-primary w-100 btn-sm fw-bold border-2"
                                                onClick={() => {
                                                    setSelectedReservation({
                                                        id: `transfer_${bed.id}`,
                                                        type: 'transfer',
                                                        patient_name: lastHistory.patientName,
                                                        patient_gender: lastHistory.gender,
                                                        patient_condition: lastHistory.condition,
                                                        origin: `Transferência Interna (Do Leito ${bed.number})`,
                                                        bed_id: bed.id
                                                    });
                                                    setShowTransferModal(true);
                                                }}
                                            >
                                                <i className="bi bi-arrow-left-right me-2"></i> Transferir
                                            </button>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {reservations.filter(r => !selectedSector || r.sector_id?.toString() === selectedSector).length === 0 ? (
                                <div className="col-span-full text-center py-12 text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                                    <h4>Nenhuma reserva encontrada {selectedSector && 'neste setor'}.</h4>
                                    <p>O fluxo está livre no momento.</p>
                                </div>
                            ) : (
                                reservations
                                    .filter(r => !selectedSector || r.sector_id?.toString() === selectedSector)
                                    .map(res => (
                                        <SmartReservationCard
                                            key={res.id}
                                            reservation={res}
                                            beds={beds}
                                            onConfirm={() => handleConfirmClick(res)}
                                            onCancel={() => handleCancel(res.id)}
                                            onAdmit={(r) => navigate('/checkin', { state: { reservation: r } })}
                                            onArchive={() => handleArchive(res.id)}
                                        />
                                    ))
                            )}
                        </div>
                    )}
                </>
            )}

            <RegulatoryConfirmationModal
                show={showModal}
                reservation={selectedReservation}
                beds={beds}
                onClose={() => setShowModal(false)}
                onConfirm={handleRegulatoryConfirm}
            />

            <TransferModal
                show={showTransferModal}
                onClose={() => setShowTransferModal(false)}
                onConfirm={handleRegulatoryConfirm} // Reuse same handler or create focused one?
                reservation={selectedReservation}
                beds={beds}
                sectors={sectors}
            />
        </div>
    );
}
